import type { MatchedPatientData, MatchedPatientInfo } from '@store/generated/admin';

/**
 * Re-export the generated type for potential matches
 */
export type PotentialMatch = MatchedPatientInfo;

/**
 * Extended type that supports both single and multiple matches
 * for backward compatibility during transition
 */
export interface ExtendedMatchedPatientData extends MatchedPatientData {
  // Array of multiple potential matches (new structure)
  potential_matches?: PotentialMatch[];

  // Helper to get matches in array format regardless of structure
  getAllMatches?: () => PotentialMatch[];
}

/**
 * Helper function to normalize patient data to always have array of matches
 */
export function normalizePatientMatches(patient: MatchedPatientData): PotentialMatch[] {
  // First check if we have the new potential_matches array
  if (patient.potential_matches && Array.isArray(patient.potential_matches)) {
    return patient.potential_matches;
  }

  // Fallback to single match if exists
  if (patient.matched_patient_id && patient.matched_patient_data) {
    return [{
      patient_id: patient.matched_patient_id,
      patient_data: patient.matched_patient_data,
      confidence: patient.matching_confidence || 0
    }];
  }

  // Check if patient has match status but no actual match data
  if (patient.import_status === 'matched') {
    return []; // Return empty array but hasMatches() will still return true due to status check
  }

  // No matches
  return [];
}

/**
 * Check if patient has any potential matches
 * This should align with the categorization logic in import-session-detail.tsx
 */
export function hasMatches(patient: MatchedPatientData): boolean {
  // Check multiple ways a patient could have matches (same as categorization logic)
  const hasNewMatches = Boolean(patient.potential_matches && patient.potential_matches.length > 0);
  const hasLegacyMatch = Boolean(patient.matched_patient_id && patient.matched_patient_data);
  const hasMatchStatus = patient.import_status === 'matched';

  return hasNewMatches || hasLegacyMatch || hasMatchStatus;
}

/**
 * Get the best match (highest confidence)
 */
export function getBestMatch(patient: MatchedPatientData): PotentialMatch | null {
  const matches = normalizePatientMatches(patient);
  if (matches.length === 0) return null;

  return matches.reduce((best, current) =>
    current.confidence > best.confidence ? current : best
  );
}

/**
 * Categorize confidence level
 */
export function getConfidenceLevel(confidence: number): {
  level: string;
  color: 'error' | 'warning' | 'info' | 'success' | 'default';
  description: string;
} {
  if (confidence >= 90) {
    return {
      level: 'very-high',
      color: 'error',
      description: 'Very likely the same patient'
    };
  }
  if (confidence >= 70) {
    return {
      level: 'high',
      color: 'warning',
      description: 'Probable match - review recommended'
    };
  }
  if (confidence >= 50) {
    return {
      level: 'medium',
      color: 'info',
      description: 'Possible match - manual review needed'
    };
  }
  if (confidence >= 30) {
    return {
      level: 'low',
      color: 'success',
      description: 'Unlikely match - probably different patients'
    };
  }
  return {
    level: 'very-low',
    color: 'default',
    description: 'Very unlikely to be the same patient'
  };
}